import { gql, type TypedDocumentNode } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { ConfirmDialog, PaginationControl } from '@repo/shared-client/components/blocks'
import {
  Badge,
  Button,
  Card,
  CardContent,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/shared-client/components/ui'
import { useNavigate } from '@tanstack/react-router'
import { ProfileInfo, ProfileMediaInfo } from '@web/src/lib/fragments'
import { useTranslation } from '@web/src/lib/intl/react'
import { REFUND_PAYMENT_REQUEST } from '@web/src/lib/mutations'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { ChevronDownIcon, ChevronUpIcon, EditIcon, ReceiptIcon, Undo2Icon } from 'lucide-react'
import { useState } from 'react'
import toast from 'react-hot-toast'
import SFProfileBanner from 'src/components/primatives/sf-profile-banner/sf-profile-banner'
import type {
  PaymentsApprovedListQuery,
  PaymentsApprovedListQueryVariables,
  RefundPaymentRequestMutation,
  RefundPaymentRequestMutationVariables,
} from 'types/graphql'

dayjs.extend(relativeTime)

export const PAYMENTS_APPROVED_LIST_QUERY: TypedDocumentNode<
  PaymentsApprovedListQuery,
  PaymentsApprovedListQueryVariables
> = gql`
  query PaymentsApprovedListQuery($page: Int, $filter: PaymentApprovedFilter, $sortBy: PaymentApprovedSortEnum) {
    paymentsApprovedListPage(page: $page, filter: $filter, sortBy: $sortBy) {
      entries {
        id
        createdAt
        updatedAt
        profileId
        days
        partnerIds
        partnerDisplayNames
        total
        refunded
        notes
        profile {
          ...ProfileInfo
          uniqueId
          email
          refunded
        }
      }
      count
      perPage
    }
  }
  ${ProfileInfo}
  ${ProfileMediaInfo}
`

type PaymentApprovedEntry = NonNullable<
  NonNullable<PaymentsApprovedListQuery['paymentsApprovedListPage']>['entries'][0]
>

interface PaymentAcceptedCardProps {
  record: PaymentApprovedEntry
  onRefunded: () => void
}

function PaymentAcceptedCard({ record, onRefunded }: PaymentAcceptedCardProps) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const [refundModal, setRefundModal] = useState(false)
  const [showRefundSpinner, setShowRefundSpinner] = useState(false)
  const navigate = useNavigate()

  const [refundPaymentRequest] = useMutation<RefundPaymentRequestMutation, RefundPaymentRequestMutationVariables>(
    REFUND_PAYMENT_REQUEST,
    {
      onCompleted: () => {
        setShowRefundSpinner(false)
        setRefundModal(false)
        toast.success(t('Refund queued'))
        onRefunded()
      },
      onError: (err) => {
        setShowRefundSpinner(false)
        toast.error(err.message)
      },
    }
  )

  const handleRefundConfirm = () => {
    setShowRefundSpinner(true)
    refundPaymentRequest({ variables: { id: record.id } })
  }

  const profileRefunded = Boolean(record.profile?.refunded)
  const canRefund = !(record.refunded || profileRefunded)

  const cardTone = record.refunded ? 'border-orange-500/40 bg-orange-500/10' : ''

  return (
    <>
      <Card className={`w-full overflow-hidden border py-0 shadow-sm ${cardTone}`}>
        <Collapsible onOpenChange={setExpanded} open={expanded}>
          <CardContent className="flex flex-col gap-0 p-0">
            <div className="flex min-h-10 w-full items-stretch">
              <CollapsibleTrigger
                render={
                  <Button
                    aria-expanded={expanded}
                    className="h-auto min-h-10 w-full flex-1 justify-start rounded-none px-3 py-2 data-[state=open]:bg-muted/50"
                    type="button"
                    variant="ghost"
                  />
                }
              >
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <div className="min-w-0 flex-1 overflow-visible">
                    <SFProfileBanner
                      disableLinks
                      lowerTitle={t('Approved — {{time}}', { time: dayjs(record.createdAt).fromNow() })}
                      profile={record.profile}
                      rightTitle={
                        profileRefunded || record.notes?.trim() ? (
                          <div className="flex flex-wrap justify-end gap-1">
                            {profileRefunded ? (
                              <Badge className="shrink-0" size="sm" variant="destructive">
                                {t('NO REFUND ALLOWED')}
                              </Badge>
                            ) : null}
                            {record.notes?.trim() ? (
                              <Badge className="shrink-0" size="sm" variant="secondary">
                                {t('NOTES')}
                              </Badge>
                            ) : null}
                          </div>
                        ) : null
                      }
                      size="md"
                    />
                  </div>
                  <div className="flex flex-col items-center gap-0">
                    <span className="text-base">{record.profile?.uniqueId}</span>
                    <span className="text-base text-muted-foreground">${record.total.toFixed(2)}</span>
                    {record.refunded ? (
                      <Badge className="mt-0.5" size="sm" variant="secondary">
                        {t('Refunded')}
                      </Badge>
                    ) : null}
                  </div>
                  {expanded ? (
                    <ChevronUpIcon className="size-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground" />
                  )}
                </div>
              </CollapsibleTrigger>
            </div>
            <CollapsibleContent>
              <div className="border-border border-t px-4 py-3">
                <div className="flex flex-col gap-1">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-muted-foreground">{t('Email')}</span>
                    <span className="text-end">{record.profile?.email ?? '—'}</span>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-muted-foreground">{t('Unique ID')}</span>
                    <span className="font-mono">{record.profile?.uniqueId ?? '—'}</span>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-muted-foreground">{t('Days')}</span>
                    <span className="font-medium">{record.days}</span>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-muted-foreground">{t('Total')}</span>
                    <span className="font-medium">${record.total.toFixed(2)}</span>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-muted-foreground">{t('Partners')}</span>
                    {record.partnerDisplayNames?.length ? (
                      <ul className="list-inside list-disc ps-4">
                        {record.partnerDisplayNames.map((name) => (
                          <li key={name}>{name}</li>
                        ))}
                      </ul>
                    ) : (
                      <span className="text-base">—</span>
                    )}
                  </div>

                  <div className="flex flex-col gap-0.5">
                    <span className="text-muted-foreground">{t('Notes')}</span>
                    {record.notes?.trim() ? (
                      <span className="whitespace-pre-wrap text-base">{record.notes}</span>
                    ) : (
                      <span className="text-base text-muted-foreground">—</span>
                    )}
                  </div>

                  <Separator className="mt-2" />
                  <div className="flex w-full shrink-0 justify-end gap-1">
                    {canRefund ? (
                      <Button
                        aria-label={t('Refund payment')}
                        onClick={() => setRefundModal(true)}
                        size="icon-sm"
                        type="button"
                        variant="ghost"
                      >
                        <Undo2Icon className="text-orange-600" />
                      </Button>
                    ) : (
                      <Tooltip>
                        <TooltipTrigger
                          render={
                            <span className="inline-flex">
                              <Button
                                aria-label={t('Refund payment')}
                                disabled
                                size="icon-sm"
                                type="button"
                                variant="ghost"
                              >
                                <Undo2Icon className="text-orange-600" />
                              </Button>
                            </span>
                          }
                        />
                        <TooltipContent>{t('Already refunded')}</TooltipContent>
                      </Tooltip>
                    )}
                    <Button
                      aria-label={t('Edit payment record')}
                      onClick={() =>
                        navigate({
                          to: '/admin/payments/approved/$paymentApprovedId/edit',
                          params: { paymentApprovedId: record.id },
                        } as never)
                      }
                      size="icon-sm"
                      type="button"
                      variant="ghost"
                    >
                      <EditIcon className="text-destructive" />
                    </Button>
                  </div>
                </div>
              </div>
            </CollapsibleContent>
          </CardContent>
        </Collapsible>
      </Card>
      <ConfirmDialog
        cancelText={t('Cancel')}
        confirmText={t('Refund')}
        loading={showRefundSpinner}
        onConfirm={handleRefundConfirm}
        onOpenChange={(e) => setRefundModal(e.open)}
        open={refundModal}
        text={t(
          'Queue a refund for this payment? Supporter terms will be adjusted and notification emails sent when the job completes.'
        )}
        title={t('Refund payment')}
        variant="basic"
      />
    </>
  )
}

type AdminPaymentAcceptedProps = {
  page: number
  paymentsApprovedListPage: PaymentsApprovedListQuery['paymentsApprovedListPage'] | undefined
  refetch: () => Promise<unknown>
  emptyTitle?: string
}

const AdminPaymentAccepted = (props: AdminPaymentAcceptedProps) => {
  const { t } = useTranslation()
  const currentPage = props.page
  const pageData = props.paymentsApprovedListPage
  const entries = pageData?.entries ?? []

  return (
    <div className="flex w-full flex-col gap-2">
      {entries.length > 0 ? (
        <div className="flex w-full flex-col gap-2">
          {entries.map((row) => (
            <PaymentAcceptedCard
              key={row.id}
              onRefunded={() => {
                props.refetch()
              }}
              record={row}
            />
          ))}
        </div>
      ) : (
        <Empty className="border-border border-dashed">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <ReceiptIcon />
            </EmptyMedia>
            <EmptyTitle>{props.emptyTitle ?? t('No accepted payments found')}</EmptyTitle>
            <EmptyDescription>{t('Try adjusting your search or filters.')}</EmptyDescription>
          </EmptyHeader>
        </Empty>
      )}
      {pageData && pageData.count > 0 ? (
        <PaginationControl count={pageData.count} page={currentPage} pageSize={pageData.perPage} />
      ) : null}
    </div>
  )
}

export default AdminPaymentAccepted
